import { useEffect, useState } from 'react';
import Link from 'next/link';
import Icon from '@common/assets';
import styles from '@components/match/matchTab/matchList/matchList.module.scss';
import { useUserInfo } from '@repository/auth/useUserInfo';
import { useGetMatchConfigStatus, useMatchProfile } from '@repository/match';
import useProfilesType from '@components/match/query/useProfilesType';
import PreferenceModal from '@components/match/profile/matchProposal/PreferenceModal';
import { LogTypeEnum } from '@/src/logging/LogType';
import { MILLISECONDS_PER_DAY } from '@domain/constant/Time';
import { AccaRouteUrl, RouteUrl, V2RouteUrl } from '@utils/routeUtil';
import useModal from '@utils/hooks/useModal';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);

export enum EmptyMatchListEnum {
  제안 = '제안',
  대기업 = '대기업',
  채용제안 = '채용제안',
  바로채용제안 = '바로채용제안',
  가고싶은기업 = '가고싶은기업',
  개월 = '개월',
  완성도 = '완성도',
}

interface OfferBtnProps {
  handleClick?: (() => void) | undefined;
}

const OfferBtn = ({ handleClick }: OfferBtnProps) => {
  return (
    <button className={cx('link')} onClick={handleClick}>
      포지션 제안받기
    </button>
  );
};

const PositionBtn = () => {
  return (
    <Link className={cx('link')} href={RouteUrl.POSITION}>
      채용 공고 확인하기
    </Link>
  );
};
const PreferenceModalBtn = () => {
  const { isModalOpen, openModal, closeModal } = useModal();
  return (
    <>
      <button className={cx('link')} onClick={openModal}>
        선호정보 업데이트하기
      </button>
      <PreferenceModal visible={isModalOpen} onClose={closeModal} />
    </>
  );
};

export type EmptyMatchListType = keyof typeof EmptyMatchListEnum;

export const EmptyMatchListContents = {
  [EmptyMatchListEnum.제안]: {
    desc: (userName: string) => (
      <div className={cx('descArea')}>
        제안 받은 포지션이 없어요.
        <span className={cx('desc')}>
          {userName}님! 1분 프로필 작성으로
          <br />
          750개 기업에게 직접 입사 제안 받아보세요.
        </span>
      </div>
    ),
    link: (handleClick?: () => void) => (
      <Link href={V2RouteUrl.PROFILE} legacyBehavior>
        <a className={cx('link')}>프로필 작성하기</a>
      </Link>
    ),
  },
  [EmptyMatchListEnum.대기업]: {
    desc: (userName: string) => (
      <div className={cx('descArea')}>
        제안 받은 포지션이 없어요.
        <span className={cx('desc')}>
          {userName}님, 역량검사 한 번으로
          <br />
          대기업 포함 750사의 채용 전형을 해결해보세요!
        </span>
      </div>
    ),
    link: (handleClick?: () => void) => (
      <Link href={AccaRouteUrl.ACCA_TEST_GATE} legacyBehavior>
        <a className={cx('link')}>역량검사 응시하기</a>
      </Link>
    ),
  },
  [EmptyMatchListEnum.채용제안]: {
    desc: (userName: string) => (
      <div className={cx('descArea')}>
        제안 받은 포지션이 없어요.
        <span className={cx('desc')}>
          {userName}님, 개발자 검사 응시하고
          <br />
          750개 기업에게 직접 입사 제안 받아보세요.
        </span>
      </div>
    ),
    link: (handleClick?: () => void) => (
      <Link href={V2RouteUrl.PHS_TEST_GATE} legacyBehavior>
        <a className={cx('link')}>검사 응시하기</a>
      </Link>
    ),
  },
  [EmptyMatchListEnum.바로채용제안]: {
    desc: (userName: string) => (
      <div className={cx('descArea')}>
        제안 받은 포지션이 없어요.
        <span className={cx('desc')}>
          이제, 입사 제안 받을 준비 끝!
          <br />
          아래 포지션 제안받기 버튼을 눌러주세요.
        </span>
      </div>
    ),
    link: (handleClick?: () => void) => <OfferBtn handleClick={handleClick} />,
  },
  [EmptyMatchListEnum.가고싶은기업]: {
    desc: (_: string, number?: number) => (
      <div className={cx('descArea')}>
        제안 받은 포지션이 없어요.
        <span className={cx('desc')}>
          제안 기다리는 동안 <br />
          잡다의 최신 채용 정보를 확인해 보세요.
        </span>
      </div>
    ),
    link: () => <PositionBtn />,
  },
  [EmptyMatchListEnum.개월]: {
    desc: (_: string, number?: number) => (
      <div className={cx('descArea')}>
        제안 받은 포지션이 없어요.
        <span className={cx('desc')}>
          마지막 프로필 업데이트가 {number}개월 전이에요. <br />
          최신 정보를 작성하면 제안 확률이 20% 증가해요
        </span>
      </div>
    ),
    link: (handleClick?: () => void) => (
      <Link className={cx('link')} href={V2RouteUrl.PROFILE}>
        프로필 업데이트하기
      </Link>
    ),
  },
  [EmptyMatchListEnum.완성도]: {
    desc: (_: string) => (
      <div className={cx('descArea')}>
        제안 받은 포지션이 없어요.
        <span className={cx('desc')}>
          내 프로필 완성도가 60% 이상이면 <br />
          입사 제안 확률이 20% 이상 증가해요.
        </span>
      </div>
    ),
    link: (handleClick?: () => void) => (
      <Link href={V2RouteUrl.PROFILE} className={cx('link')}>
        프로필 작성하기
      </Link>
    ),
  },
};

interface EmptyMatchListProps {
  handleMatchingConfigYn: () => void;
}

const EmptyMatchList = ({ handleMatchingConfigYn }: EmptyMatchListProps) => {
  const { data: userData } = useUserInfo();
  const isLogin = !!userData?.id;
  const userName = userData?.name || '회원';
  const { data: matchConfigStatus } = useGetMatchConfigStatus(isLogin);
  const { data: profileData } = useMatchProfile(isLogin);
  const { data: profilesType } = useProfilesType(isLogin);
  const [emptyType, setEmptyType] = useState<EmptyMatchListType | null>(null);
  const lastProfileUpdateDateTime = matchConfigStatus?.lastProfileUpdateDateTime || new Date();

  const monthsBetweenDates = (dateA: Date, dateB: Date) => {
    // dateA에서 dateB를 뺀 밀리초(ms) 차이 계산
    const timeDifference = dateB.getTime() - dateA.getTime();

    // 밀리초를 월로 변환 (평균 30.44일로 계산)
    const millisecondsPerMonth = 30.44 * MILLISECONDS_PER_DAY;
    const months = timeDifference / millisecondsPerMonth;

    return Math.floor(months); // 소수점 이하는 버리고 정수로 반환
  };

  const getIsMonthsPassed = (dateA: Date, dateB: Date) => {
    // date1에서 1개월 후의 날짜를 계산
    const threeMonthsLater = new Date(dateA);
    threeMonthsLater.setMonth(threeMonthsLater.getMonth() + 1);

    // date2와 threeMonthsLater를 비교
    return dateB >= threeMonthsLater;
  };

  const descEl = emptyType
    ? EmptyMatchListContents[emptyType]?.desc(
        userName,
        monthsBetweenDates(new Date(lastProfileUpdateDateTime), new Date()) || 1,
      )
    : null;
  const linkEl = emptyType ? EmptyMatchListContents[emptyType]?.link(handleMatchingConfigYn) : null;

  useEffect(() => {
    if (!matchConfigStatus) return;
    if (!profilesType) return;
    if (!profileData) return;

    const {
      developerMatchingConfigAvailableYn,
      generalMatchingConfigAvailableYn,
      profileRequiredCompleteYn,
      accCompleteYn,
      paccCompleteYn,
      matchingConfigYn,
    } = matchConfigStatus;
    const { generalYn, developerYn } = profilesType;
    const {
      profile: { completionRate },
    } = profileData;

    // 인재풀인지 아닌지 판단 ( 개발자 역량검사 완료, 프로필 필수값 입력 && 매칭 제안 ON ) || ( 일반 역량검사 완료, 프로필 필수값 입력 && 매칭 제안 ON )
    const isMatchingServiceYn =
      (developerMatchingConfigAvailableYn && matchingConfigYn) ||
      (generalMatchingConfigAvailableYn && matchingConfigYn);

    // 인재풀인 경우
    if (isMatchingServiceYn) {
      // 마지막 프로필 업데이트 날짜
      const lastProfileUpdateDateTime = matchConfigStatus?.lastProfileUpdateDateTime || new Date();

      // 마지막 프로필 업데이트 날짜로부터 1개월이 지났는지 판단
      const isMonthsPassed = getIsMonthsPassed(new Date(lastProfileUpdateDateTime), new Date());

      // 프로필 업데이트 > 1개월인 경우
      if (isMonthsPassed) {
        setEmptyType(EmptyMatchListEnum.개월);
        return;
      }

      // 프로필 완성도가 60% 미만인 경우
      if (completionRate < 60) {
        setEmptyType(EmptyMatchListEnum.완성도);
        return;
      }

      //프로필 업데이트 < 1개월 + 프로필 작성률 > 60%인 경우
      setEmptyType(EmptyMatchListEnum.가고싶은기업);
      return;
    }
    // 인재풀이 아닌 경우
    else {
      // 선호 직군직무 입력 x 유저
      if (!generalYn && !developerYn) {
        setEmptyType(EmptyMatchListEnum.제안);
        return;
      }

      // 내 프로필 필수 입력 x && 역량검사/개발자 역량검사 응시 x 유저
      // 선호 직군 일반, 선호 직군 개발, 선호 직군 둘 다 선택한 경우
      if (!profileRequiredCompleteYn && !(accCompleteYn || paccCompleteYn) && generalYn && developerYn) {
        setEmptyType(EmptyMatchListEnum.제안);
        return;
      }

      // 내 프로필 필수 입력 x && 역량검사/개발자 역량검사 응시 o 유저
      // 선호 직군 일반, 선호 직군 개발, 선호 직군 둘 다 선택한 경우
      if (!profileRequiredCompleteYn && (accCompleteYn || paccCompleteYn) && generalYn && developerYn) {
        setEmptyType(EmptyMatchListEnum.제안);
        return;
      }

      // 내 프로필 필수 입력 o && 역량검사/개발자 역량검사 응시 x 유저
      // 선호 직군 일반 선택한 경우
      if (profileRequiredCompleteYn && !(accCompleteYn || paccCompleteYn) && generalYn) {
        setEmptyType(EmptyMatchListEnum.대기업);
        return;
      }

      // 내 프로필 필수 입력 o && 역량검사/개발자 역량검사 응시 x 유저
      // 선호 직군 개발인 경우
      if (profileRequiredCompleteYn && !(accCompleteYn || paccCompleteYn) && developerYn) {
        setEmptyType(EmptyMatchListEnum.채용제안);
        return;
      }

      // 내 프로필 필수 입력 o && 역량검사/개발자 역량검사 응시 x 유저
      // 선호 직군 둘 다 선택한 경우
      if (profileRequiredCompleteYn && !(accCompleteYn || paccCompleteYn) && generalYn && developerYn) {
        setEmptyType(EmptyMatchListEnum.대기업);
        return;
      }

      // 내 프로필 필수 입력 o && 역량검사/개발자 역량검사 응시 o && 토글만 off한 유저
      // 선호 직군 일반, 선호 직군 개발, 선호 직군 둘 다 선택한 경우
      if (
        profileRequiredCompleteYn &&
        (accCompleteYn || paccCompleteYn) &&
        !matchingConfigYn &&
        (generalYn || developerYn)
      ) {
        setEmptyType(EmptyMatchListEnum.바로채용제안);
        return;
      }
    }

    setEmptyType(EmptyMatchListEnum.제안);
  }, [matchConfigStatus, profilesType, profileData]);

  return (
    <div className={cx('emptyArea')}>
      <Icon name="joyEmpty" width={96} height={96} />
      <div className={cx('emptyWrap')}>
        {descEl}
        <div id={LogTypeEnum.log_match_empty_position}>{linkEl}</div>
      </div>
    </div>
  );
};

export default EmptyMatchList;
